import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, Calendar, Bell, CheckCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppointmentAPI, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useFormatters } from "@/lib/format";
import { toast } from "sonner";

const STATUSES = [
    { code: "scheduled",   label: "Scheduled",  pill: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300" },
    { code: "confirmed",   label: "Confirmed",  pill: "bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300" },
    { code: "checked_in",  label: "Checked-in", pill: "bg-brand-redSoft text-brand-redDeep dark:bg-brand-red/15 dark:text-brand-red" },
    { code: "converted",   label: "Converted",  pill: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" },
    { code: "cancelled",   label: "Cancelled",  pill: "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300" },
    { code: "no_show",     label: "No-show",    pill: "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300" },
];

export default function AppointmentsPage() {
    const { permissions } = useAuth();
    const { formatDateTime, formatTime } = useFormatters();
    const canCreate = permissions.includes("appointment:create");
    const canNotify = permissions.includes("appointment:notify");

    const [appts, setAppts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("");
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(new Set());

    async function load() {
        setLoading(true);
        try {
            const params = { page: 1, page_size: 200 };
            if (status) params.status = status;
            const r = await AppointmentAPI.list(params);
            setAppts(r.items || []);
        } catch (e) { toast.error(extractError(e)); }
        finally { setLoading(false); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [status]);

    const filtered = useMemo(() => {
        if (!q) return appts;
        const needle = q.toLowerCase();
        return appts.filter((a) =>
            a.customer?.display_name?.toLowerCase().includes(needle) ||
            a.vehicle?.registration_number?.toLowerCase().includes(needle) ||
            a.customer?.phone?.includes(needle)
        );
    }, [appts, q]);

    function toggle(id) {
        setSelected((s) => {
            const n = new Set(s);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    }

    async function bulkRemind() {
        const ids = Array.from(selected);
        let ok = 0, fail = 0;
        for (const id of ids) {
            try { await AppointmentAPI.sendReminder(id); ok++; }
            catch { fail++; }
        }
        toast.success(`Reminders queued: ${ok} ok${fail ? `, ${fail} failed` : ""}`);
        setSelected(new Set());
        load();
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="appointments-page">
            <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Appointments</h1>
                    <p className="mt-1 text-sm text-zinc-500">Bookings across all branches. Switch to the <Link to="/schedule" className="text-brand-redDeep hover:underline">calendar</Link> for drag-drop scheduling.</p>
                </div>
                <div className="flex items-center gap-2">
                    {selected.size > 0 && canNotify && (
                        <Button variant="outline" onClick={bulkRemind} data-testid="appt-bulk-remind">
                            <Bell className="mr-1 h-4 w-4" /> Remind {selected.size}
                        </Button>
                    )}
                    {canCreate && (
                        <Link to="/appointments/new">
                            <Button className="bg-brand-red text-white hover:bg-brand-redDeep" data-testid="appt-new-btn"><Plus className="mr-1 h-4 w-4" /> New appointment</Button>
                        </Link>
                    )}
                </div>
            </header>

            <Card className="p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <Input placeholder="Search by customer / phone / registration…" value={q} onChange={(e) => setQ(e.target.value)} className="sm:max-w-sm" data-testid="appt-search" />
                    <div className="flex flex-wrap gap-1">
                        <FilterChip active={!status} onClick={() => setStatus("")}>All</FilterChip>
                        {STATUSES.map((s) => (
                            <FilterChip key={s.code} active={status === s.code} onClick={() => setStatus(s.code)} testid={`appt-filter-${s.code}`}>{s.label}</FilterChip>
                        ))}
                    </div>
                </div>
            </Card>

            {loading ? (
                <Card className="grid place-items-center p-12 text-sm text-zinc-500"><Loader2 className="h-5 w-5 animate-spin" /></Card>
            ) : filtered.length === 0 ? (
                <Card className="grid place-items-center p-12 text-sm text-zinc-500">No appointments in this window.</Card>
            ) : (
                <Card className="overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="border-b bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500 dark:bg-zinc-900">
                            <tr>
                                <th className="px-3 py-3 w-8"></th>
                                <th className="px-4 py-3">When</th>
                                <th className="px-4 py-3">Customer</th>
                                <th className="px-4 py-3">Vehicle</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((a) => {
                                const s = STATUSES.find((x) => x.code === a.status);
                                return (
                                    <tr key={a.id} className="border-b last:border-b-0" data-testid={`appt-row-${a.id}`}>
                                        <td className="px-3 py-3">
                                            <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggle(a.id)} data-testid={`appt-check-${a.id}`} />
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs" data-testid="appointment-time-display">
                                            <div>{formatDateTime(a.scheduled_start)}</div>
                                            <div className="text-zinc-500">→ {formatTime(a.scheduled_end)}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{a.customer?.display_name || "—"}</div>
                                            <div className="text-xs text-zinc-500 font-mono">{a.customer?.phone}</div>
                                        </td>
                                        <td className="px-4 py-3 font-mono text-sm">
                                            {a.vehicle?.registration_number || "—"}
                                            <div className="text-xs text-zinc-500 font-sans">{a.vehicle?.make} {a.vehicle?.model}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${s?.pill || ""}`}>
                                                {s?.label || a.status}
                                            </span>
                                            {a.priority === "high" && <span className="ml-1 inline-flex rounded bg-brand-red/15 px-1.5 py-0.5 text-[10px] uppercase text-brand-redDeep">high</span>}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <Link to={`/appointments/${a.id}`} className="text-xs text-brand-redDeep hover:underline" data-testid={`appt-open-${a.id}`}>Open →</Link>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Card>
            )}
        </div>
    );
}

function FilterChip({ active, onClick, children, testid }) {
    return (
        <button type="button" onClick={onClick} data-testid={testid}
            className={`rounded-md px-2.5 py-1 text-xs uppercase tracking-wider transition-colors ${active ? "bg-brand-red text-white" : "border border-zinc-200 text-zinc-600 hover:border-zinc-300 dark:border-zinc-800 dark:text-zinc-400"}`}>
            {children}
        </button>
    );
}
